import { Component, Input } from '@angular/core';
import { EventDoc } from '../../models/event.model';
import { EventPriceDisplay, eventPrice } from '../event-utils';

/**
 * The white price chip — iOS `priceView` / `Event.priceFormat`.
 *
 * Three shapes: "Free", a bare amount, or a small "from" label stacked over the amount. The chip is
 * white with dark text on every surface (iOS parity), so it deliberately does not follow
 * `--ev-on-surface`; only its corner treatment changes per host, via the `corners` input.
 *
 * Always renders, including for a free event: "Free" is information a guest wants, and roughly 40%
 * of documents carry no prices at all, so suppressing it would leave most cards with a blank corner.
 */
@Component({
    selector: 'app-event-price-pill',
    template: `
    <span class="ev-price" [class.ev-price-hero]="corners === 'hero'">
      <ng-container [ngSwitch]="price.kind">
        <span *ngSwitchCase="'free'" class="ev-price-amount">{{ 'events.free' | translate }}</span>

        <ng-container *ngSwitchCase="'from'">
          <span class="ev-price-from">{{ 'events.from' | translate }}</span>
          <span class="ev-price-amount">{{ price.amount }}</span><span
            class="ev-price-currency" *ngIf="price.currency"> {{ price.currency }}</span>
        </ng-container>

        <ng-container *ngSwitchCase="'single'">
          <span class="ev-price-amount">{{ price.amount }}</span><span
            class="ev-price-currency" *ngIf="price.currency"> {{ price.currency }}</span>
        </ng-container>
      </ng-container>
    </span>
  `,
    styles: [`
    :host { display: inline-flex; }
    .ev-price {
      display: inline-flex;
      flex-direction: column;
      align-items: flex-end;
      justify-content: center;
      background: var(--ev-price-surface, #FFFFFF);
      color: var(--ev-on-price-surface, #0C0C0C);
      padding: 8px;
      /* Card variant: notched into the image's bottom-right corner, per iOS. */
      border-radius: 10px 0 10px 0;
      box-shadow: 0 1px 4px var(--ev-shadow, rgba(0, 0, 0, 0.42));
      line-height: 1.1;
      white-space: nowrap;
    }
    /* Details-hero variant: a single large top-left round, flush to the bottom-right of the image. */
    .ev-price.ev-price-hero {
      border-radius: 28px 0 0 0;
      padding: 14px 16px;
    }
    .ev-price-from {
      font-size: 12px;
      font-weight: 500;
    }
    .ev-price-amount {
      font-size: 16px;
      font-weight: 700;
    }
    .ev-price-currency {
      font-size: 12px;
      font-weight: 400;
    }
  `],
    standalone: false
})
export class EventPricePillComponent {
  @Input() event!: EventDoc;
  /** `card` notches the chip into an image corner; `hero` is the larger details-screen treatment. */
  @Input() corners: 'card' | 'hero' = 'card';

  get price(): EventPriceDisplay {
    return eventPrice(this.event);
  }
}
